import React from 'react';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface FileStatus {
  filename: string;
  status: string;
}

interface CN19FileStatusTableProps {
  title: string;
  fileNames: FileStatus[];
}

// Styled components
const StyledTableContainer = styled('div')(() => ({
  overflowX: 'auto',
  border: '1px solid #E1E8ED',
  borderRadius: '8px',
  backgroundColor: '#FFFFFF',
  marginBottom: '24px',
}));

const StyledTable = styled(Table)(() => ({
  minWidth: 400,
}));

const HeaderCell = styled('th')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '12px',
  backgroundColor: '#E7EFF5 !important',
  textAlign: 'left',
  color: '#063142',
  padding: theme.spacing(1, 1.5),
  border: '1px solid #D1D9E0',
}));

const DataCell = styled('td')(({ theme }) => ({
  fontSize: '11px',
  backgroundColor: '#FFFFFF',
  padding: theme.spacing(0.75, 1.5),
  border: '1px solid #E1E8ED',
  fontFamily: 'monospace',
}));

function CN19FileStatusTable({ title, fileNames }: CN19FileStatusTableProps): React.ReactElement {
  const validFiles = fileNames.filter(file => file.filename.trim() !== '');

  return (
    <Box sx={{ mb: 3,  maxWidth: '600px' }} data-testid={`cn19-file-status-table-${title.toLowerCase()}`}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#063142', mb: 2 }} data-testid={`cn19-file-status-title-${title.toLowerCase()}`}>
        {title}
      </Typography>
      <StyledTableContainer data-testid={`cn19-file-status-container-${title.toLowerCase()}`}>
        <StyledTable size="small" data-testid={`cn19-file-status-table-${title.toLowerCase()}`}>
          <TableHead data-testid={`cn19-file-status-head-${title.toLowerCase()}`}>
            <TableRow data-testid={`cn19-file-status-header-row-${title.toLowerCase()}`}>
              <HeaderCell style={{ minWidth: '400px' }} data-testid={`cn19-file-status-header-filename-${title.toLowerCase()}`}>Filename</HeaderCell>
              <HeaderCell style={{ minWidth: '100px' }} data-testid={`cn19-file-status-header-status-${title.toLowerCase()}`}>Status</HeaderCell>
            </TableRow>
          </TableHead>
          <TableBody data-testid={`cn19-file-status-body-${title.toLowerCase()}`}>
            {validFiles.length > 0 ? (
              validFiles.map((file, index) => (
                <TableRow key={`${title.toLowerCase()}-${index}`} data-testid={`cn19-file-status-row-${title.toLowerCase()}-${index}`}>
                  <DataCell data-testid={`cn19-file-status-cell-filename-${title.toLowerCase()}-${index}`}>
                    {file.filename}
                  </DataCell>
                  <DataCell data-testid={`cn19-file-status-cell-status-${title.toLowerCase()}-${index}`} style={{ textAlign: 'center' }}>
                    <span style={{ color: '#28a745', fontWeight: 600 }}>{file.status}</span>
                  </DataCell>
                </TableRow>
              ))
            ) : (
              <TableRow data-testid={`cn19-file-status-empty-row-${title.toLowerCase()}`}>
                <DataCell colSpan={2} style={{ fontStyle: 'italic', color: '#666', textAlign: 'center' }} data-testid={`cn19-file-status-empty-cell-${title.toLowerCase()}`}>
                  No {title.toLowerCase()} files
                </DataCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </Box>
  );
}

export default CN19FileStatusTable; 